import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../context/alert';
import { AuthResponse } from '../../types/api/auth';
import { generateTotpSetup, verifyTotpSetup, verifyTotp } from '../../services/totp';
import { TotpSetupVerificationRequest } from '../../types/api/totp';
import { useUserContext } from '../../context/user';
import { Button, Stack, Text } from '@primer/react'
import CodeInput from '../basic/inputs/CodeInput';

interface TwoFactorAuthFormProps {
    authData: AuthResponse
    onNeedUpdateNewTempToken: () => void;
    onDismiss: () => void;
    setTitle?: (title: string) => void
}

export const TwoFactorAuthForm = (props: TwoFactorAuthFormProps) => {
    const { setJwtToken, setJwtRefreshToken, jwtToken, setIsLoadingProfile, isFetchingSetup } = useUserContext();
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [setupImageQRCode, setSetupImageQRCode] = useState('');
    const [isDisplayedNeedSetupQR, setIsDisplayedNeedSetupQR] = React.useState<boolean | undefined>(undefined);

    const { setError: setAlertError } = useAlert();

    const handleChange = (currentCode: string) => {
        if (error) setError('');
        setCode(currentCode);
    };
    // Setup
    const { mutate: totpSetup, isPending: isPendingSetup } = useMutation({
        mutationFn: generateTotpSetup,
        onSuccess: (data) => {
            if (data.qrCodeBase64) {
                setSetupImageQRCode(data.qrCodeBase64)
            }
        },
        onError: (error: any) => {
            setError(error.message);
        }
    });

    const saveAuthData = ({ accessToken, refreshToken }: { accessToken: string; refreshToken: string }) => {
        if (accessToken && refreshToken) {
            // Флаг для редиректа по роли, что бы она ожидала окончания загрузки
            setIsLoadingProfile(true)
            setJwtToken(accessToken);
            setJwtRefreshToken(refreshToken);
            // Дальше подхватится в редиректе
        }
    }

    // Setup Verify
    const { mutate: verifySetup, isPending: isPendingVerifySetup } = useMutation({
        mutationFn: ({ verificationData, jwt }: { verificationData: TotpSetupVerificationRequest, jwt: string }) => verifyTotpSetup(verificationData, jwt),
        onSuccess: (data) => {
            if (data) {
                if (data.enabled) {
                    const { accessToken, refreshToken } = props.authData;
                    (accessToken && refreshToken) && saveAuthData({ accessToken, refreshToken })
                } else {
                    setError('Не удается подтвердить настройку 2FA');
                }
            }
        },
        onError: (error: any) => {
            switch (error.status) {
                case 401:
                    setAlertError(error.message);
                    props.onNeedUpdateNewTempToken()
                    break;
                case 499:
                    setError('Введен неверный код, введите новый');
                    break;
                default:
                    setError(error.message);
            }
        }
    });

    // Code Verify
    const { mutate: verifyCode, isPending: isPendingVerifyCode } = useMutation({
        mutationFn: verifyTotp,
        onSuccess: (data) => {
            data && saveAuthData(data)
        },
        onError: (error: any) => {
            switch (error.status) {
                case 401:
                    setAlertError(error.message);
                    props.onNeedUpdateNewTempToken()
                    break;
                case 499:
                    setError('Введен неверный код, введите новый');
                    break;
                default:
                    setError(error.message);
            }
        }
    });

    useEffect(() => {
        if (props.authData.totpRequired) {
            // waiting entering code
            setIsDisplayedNeedSetupQR(false)
        } else {
            const accessToken = props.authData.accessToken
            if (accessToken) {
                if (isDisplayedNeedSetupQR === true) {
                    setIsDisplayedNeedSetupQR(false)
                } else {
                    totpSetup(accessToken)
                    if (props.setTitle) {
                        props.setTitle("Включение двухфакторной аутентификации")
                    }

                    setIsDisplayedNeedSetupQR(true)
                }
            }
        }
    }, [])

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        const temporaryToken = props.authData.temporaryToken

        if (props.authData.totpRequired) {
            if (temporaryToken) {
                verifyCode({ temporaryToken, code })
            }
        } else {
            const accessToken = props.authData.accessToken
            if (accessToken) {
                const verificationData: TotpSetupVerificationRequest = {
                    code: code
                };
                verifySetup({ verificationData: verificationData, jwt: accessToken });
            }
        }
    };

    const nextSetupCodeStep = () => {
        setIsDisplayedNeedSetupQR(false)
        if (props.setTitle) {
            props.setTitle('Двухфакторная авторизация')
        }
    }

    useEffect(() => {
    }, [isDisplayedNeedSetupQR]);

    if (isDisplayedNeedSetupQR === undefined) {
        return <div></div>;
    }


    if (isDisplayedNeedSetupQR) {
        return (
            <Stack gap='none'>
                <Text as="span" weight='normal' className='text-[14px] text-aloix-grey whitespace-pre-line mb-[14px] mt-1'>Используйте приложение Google Authentificator. Отсканируйте QR - код и перейдите на следующий шаг</Text>
                {
                    setupImageQRCode && <img
                        className="aspect-square w-[160px] mx-auto mt-[8px]"
                        alt='QR'
                        src={setupImageQRCode}></img>
                }
                <Stack justify="space-between" direction="horizontal" className='mt-[18px]'>
                    <div></div>
                    <Stack direction="horizontal" gap='normal'>
                        <Button
                            onClick={props.onDismiss}
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            onClick={nextSetupCodeStep}
                        >
                            Продолжить
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        );
    }

    return (
        <form onSubmit={submit}>
            <Stack gap='none'>
                <Text as="span" weight='normal' className='text-[14px] text-aloix-grey whitespace-pre-line mb-[14px] mt-1'>Введите код из приложения Google Authenticator, чтобы авторизоваться</Text>
                <CodeInput
                    onComplete={() => { }}
                    onChange={handleChange}
                    error={error.length > 0}
                    disabled={false}
                />
                {error && <Text as="span" weight='bold' className='text-[12px] text-aloix-red font-semibold mt-[13px]'>{error}</Text>
                }
                <Stack justify="space-between" direction="horizontal" className={error ? 'mt-[50px]' : 'mt-[89px]'}>
                    <div></div>
                    <Stack direction="horizontal" gap='normal'>
                        <Button
                            onClick={props.onDismiss}
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={isPendingSetup || isPendingVerifyCode || isPendingVerifySetup || isFetchingSetup}
                            disabled={isPendingSetup || isPendingVerifyCode || isPendingVerifySetup || isFetchingSetup || !code.trim() || code.trim().length !== 6}>
                            Подтвердить
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </form>
    );
};